import React from 'react'
import {Box, Button, Flex, Spinner, Text} from '../../utils/chakraMonomorphic'

import {PageHeader} from '../../components/PageHeader/PageHeader'
import {PageTemplate} from '../../templates/PageTemplate/PageTemplate'
import {useRequiredAuth} from '../../stores/AuthStore'
import {useTerm} from '../../hooks/useTerm'
import {useTaggedPhotoReport} from '../../hooks/useTaggedPhotoReport'
import {useSessions} from '../../queries/useSessions'
import {useDivisions} from '../../queries/useDivisions'
import {useBunkPlans} from '../../queries/useBunkPlans'
import {ReportFilterSelection} from '../../types'

import copy from './copy.json'
import {FilterOption, ReportFilters} from './subcomps/ReportFilters'
import {PrintableReport} from './subcomps/PrintableReport'
import {buildCsvFilename, buildReportCsv, downloadCsv} from './csv'

const EMPTY_SELECTION: ReportFilterSelection = {
  sessionIds: [],
  divisionIds: [],
  bunkPlanId: null,
}

/* eslint-disable @typescript-eslint/no-explicit-any */
const toOptions = (pages: any[] | undefined): FilterOption[] =>
  (pages ?? [])
    .flatMap((page) => page?.results ?? [])
    .map((record: {ID: string; name: string}) => ({
      id: Number(record.ID),
      name: record.name,
    }))
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * CampMinder-employee-only Tagged Photo Report.
 *
 * Answers, for the campers matching a session/division/bunk-plan selection in
 * the camp's current season: does a training photo exist for them AT THIS CAMP,
 * how many published tags do they have this season, and how long since they were
 * last tagged. Delivery is the browser print dialog, so the same artifact serves
 * a printer and a PDF with no new dependency (D-E).
 *
 * Visibility is gated in three places, only the last of which is a security
 * boundary:
 *   - `useIsCampMinderEmployee()` and the ConfigCat flag hide the nav item and
 *     the route (see SideMenuTemplate / Router). Both are UX affordances.
 *   - The faces coverage endpoint enforces the authoritative `campminderstaff`
 *     check and returns 403. That is the real gate, and AC-13 is what the user
 *     sees when it fires.
 */
export const TaggedPhotoReportView: React.FC<
  React.PropsWithChildren
> = (): React.ReactElement => {
  const {client} = useRequiredAuth()
  const season = client.currentSeasonID
  const bunkTerm = useTerm('bunk')

  const [filters, setFilters] =
    React.useState<ReportFilterSelection>(EMPTY_SELECTION)

  const sessionsQuery = useSessions({season})
  const divisionsQuery = useDivisions()
  const bunkPlansQuery = useBunkPlans({season})

  const {run, status, error, rows, showBunkColumn} = useTaggedPhotoReport()

  const isLoadingFilters =
    sessionsQuery.isLoading ||
    divisionsQuery.isLoading ||
    bunkPlansQuery.isLoading

  const handleSubmit = React.useCallback((): void => {
    // The hook owns every failure mode; nothing here can throw to the tree.
    run(filters)
  }, [filters, run])

  // AC-19. There is a file to produce only once a run has succeeded AND it
  // matched at least one camper; before that, and after an empty result, the
  // control is dead and the page says what it already says for the print path.
  const canExport = status === 'ready' && rows.length > 0

  const handleExport = React.useCallback((): void => {
    if (!canExport) return

    // Same rows, same formatters, no second read (D-J).
    downloadCsv(
      buildCsvFilename(),
      buildReportCsv({
        rows,
        showBunkColumn,
        bunkTerm: bunkTerm.singular().titleCase().value(),
      }),
    )
  }, [bunkTerm, canExport, rows, showBunkColumn])

  const renderStatus = (): React.ReactElement | null => {
    if (status === 'idle') {
      return (
        <Text
          fontSize='sm'
          color='gray.700'
          data-testid='tagged-photo-report-idle'>
          {copy.states.idle}
        </Text>
      )
    }

    if (status === 'loading') {
      return (
        <Flex
          alignItems='center'
          gap={3}
          data-testid='tagged-photo-report-loading'>
          <Spinner size='sm' />
          <Text fontSize='sm'>{copy.states.loading}</Text>
        </Flex>
      )
    }

    if (status === 'empty') {
      return (
        <Text
          fontSize='sm'
          color='gray.700'
          data-testid='tagged-photo-report-empty'>
          {copy.states.empty}
        </Text>
      )
    }

    if (status === 'error') {
      // Whatever went wrong, the message is the one the service layer wrote for
      // that specific failure — a 403 says "not authorized" (AC-13), a coverage
      // or roster failure says the report could not be produced (AC-14, AC-15).
      return (
        <Text
          color='red.600'
          fontSize='sm'
          data-testid='tagged-photo-report-error'>
          {error}
        </Text>
      )
    }

    return (
      <Text
        fontSize='sm'
        color='gray.700'
        data-testid='tagged-photo-report-ready'>
        {copy.states.ready}
      </Text>
    )
  }

  return (
    <PageTemplate
      header={
        <PageHeader
          title={copy.title}
          logContext='TaggedPhotoReport'
          description={copy.description}
        />
      }
      width={['100%']}
      maxWidth={['1030px']}
      borderX={['0px']}>
      <Flex direction='column' px='.75em'>
        <Box borderTop='1px' mt={3} mb={6} />

        <ReportFilters
          sessions={toOptions(sessionsQuery.data?.pages)}
          divisions={toOptions(divisionsQuery.data?.pages)}
          bunkPlans={toOptions(bunkPlansQuery.data?.pages)}
          isLoading={isLoadingFilters}
          isRunning={status === 'loading'}
          value={filters}
          onChange={setFilters}
          onSubmit={handleSubmit}
        />

        <Box mt={6}>{renderStatus()}</Box>

        <Box mt={4}>
          <Button
            variant='outline'
            size='sm'
            isDisabled={!canExport}
            onClick={handleExport}
            title={copy.export.help}
            data-testid='tagged-photo-report-export'>
            {copy.export.label}
          </Button>
        </Box>

        {/* Rendered only once a run has succeeded, so the print target can never
            be a stale table from a previous selection. */}
        {status === 'ready' && (
          <PrintableReport
            rows={rows}
            showBunkColumn={showBunkColumn}
            bunkColumnHeading={bunkTerm.singular().titleCase().value()}
            campName={client.name}
            season={season}
          />
        )}
      </Flex>
    </PageTemplate>
  )
}
